import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  BotMessageSquare,
  Building2,
  Database,
  FileText,
  LayoutDashboard,
  MessageCircle,
  MessageSquare,
  Plug,
  Settings,
  Terminal,
  ThumbsUp,
  Wrench,
  Zap,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Logo } from '@/components/layout/Logo';

interface NavItem {
  to: string;
  labelKey: string;
  icon: typeof LayoutDashboard;
  stub?: boolean;
}

interface NavSection {
  labelKey: string;
  items: NavItem[];
}

const SECTIONS: NavSection[] = [
  {
    labelKey: 'nav.section.pilotage',
    items: [
      { to: '/', labelKey: 'nav.overview', icon: LayoutDashboard },
      { to: '/organization', labelKey: 'nav.organization', icon: Building2 },
      { to: '/chat', labelKey: 'nav.chat', icon: MessageSquare },
      { to: '/widget', labelKey: 'nav.widget', icon: MessageCircle },
    ],
  },
  {
    labelKey: 'nav.section.supervision',
    items: [
      { to: '/audit', labelKey: 'nav.audit', icon: Activity },
      { to: '/feedback', labelKey: 'nav.feedback', icon: ThumbsUp },
      { to: '/proposals', labelKey: 'nav.proposals', icon: FileText },
    ],
  },
  {
    labelKey: 'nav.section.configuration',
    items: [
      { to: '/integrations', labelKey: 'nav.integrations', icon: Plug },
      { to: '/tools', labelKey: 'nav.tools', icon: Wrench },
      { to: '/rag', labelKey: 'nav.rag', icon: Database },
      { to: '/mcp', labelKey: 'nav.mcp', icon: Terminal },
      { to: '/google-chat', labelKey: 'nav.googleChat', icon: BotMessageSquare },
      { to: '/cache', labelKey: 'nav.cache', icon: Zap },
      { to: '/system', labelKey: 'nav.system', icon: Settings },
    ],
  },
];

export function Sidebar(): JSX.Element {
  const { t } = useTranslation();
  return (
    <aside className="hidden w-64 shrink-0 flex-col border-r border-sidebar-border bg-sidebar md:flex">
      <div className="flex h-16 items-center gap-2.5 px-5">
        <Logo className="h-7" />
      </div>

      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {SECTIONS.map((section) => (
          <div key={section.labelKey} className="mb-5">
            <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(section.labelKey)}
            </p>
            <div className="space-y-0.5">
              {section.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    cn(
                      'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      isActive
                        ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                        : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground',
                    )
                  }
                >
                  {({ isActive }) => (
                    <>
                      <span
                        className={cn(
                          'absolute left-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-r-full bg-primary transition-all',
                          isActive ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <item.icon
                        className={cn(
                          'h-[18px] w-[18px] shrink-0 transition-colors',
                          isActive
                            ? 'text-primary'
                            : 'text-muted-foreground group-hover:text-sidebar-foreground',
                        )}
                      />
                      <span className="flex-1">{t(item.labelKey)}</span>
                      {item.stub ? (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                          {t('nav.soon')}
                        </span>
                      ) : null}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-sidebar-border px-5 py-3.5">
        <div className="flex items-center gap-2 text-[11px] font-medium text-muted-foreground">
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />
          <span>v0.1.0</span>
          <span className="text-muted-foreground/40">·</span>
          <span className="capitalize">{import.meta.env.MODE}</span>
        </div>
      </div>
    </aside>
  );
}
